import Select, { OptionProps } from 'components/shared/Select'
import Info from 'components/shared/Info'
import { defineMessages, useIntl } from 'react-intl'
import { OriginalSettings } from 'routes/Upsell/Bus/fields/BusDatesAndHours'
import { useEffect, useState } from 'react'

interface BusHoursProps {
    options: OptionProps[]
    hour: string
    setHour: (hour: string) => void
    setSeatsLimit: (limit: number) => void
    id: string
    isNoTickets: boolean
    selectedTicketsAmount: number
    originalSettings?: OriginalSettings
    routeIndex?: number
    testIdPrefix?: string
}

const BusHours = ({
    options,
    hour,
    setHour,
    setSeatsLimit,
    id,
    isNoTickets,
    selectedTicketsAmount,
    originalSettings,
    routeIndex,
    testIdPrefix,
}: BusHoursProps) => {
    const { formatMessage } = useIntl()
    const [localSeatsLimit, setLocalSeatsLimit] = useState(0)

    const calculateSelectedOptionSeats = (hour: string) => {
        const selectedOption = options.find((option) => option.option === hour)
        let optionSeats = selectedOption?.optionData || 0

        if (originalSettings) {
            const { hour: originalHour, quantity } = originalSettings
            const isActuallySelectedOption = originalHour === hour
            // add selected tickets to hour available limit if we are editing quantity on the same already selected course
            optionSeats = isActuallySelectedOption
                ? optionSeats + (quantity || 0)
                : optionSeats
        }

        return optionSeats
    }

    const selectorPlaceholder = formatMessage({
        id: 'global.input.choose_hour_placeholder',
        defaultMessage: 'Wybierz godzinę',
    })

    const handleChange = (hour: string) => {
        setHour(hour)
        const newSeatsLimit = calculateSelectedOptionSeats(hour)
        setSeatsLimit(newSeatsLimit)
        setLocalSeatsLimit(newSeatsLimit)
    }

    useEffect(() => {
        if (hour === '') return
        const newSeatsLimit = calculateSelectedOptionSeats(hour)
        if (newSeatsLimit === localSeatsLimit) return
        setLocalSeatsLimit(newSeatsLimit)
        setSeatsLimit(newSeatsLimit)
    }, [options])

    const optionsWithRecalculatedAvailability = options.map((item) => {
        const { option, isDisabled, optionInfo, optionData } = item
        let recalculatedOptionSeats = optionData || 0
        let recalculatedOptionInfo = optionInfo
        let isDisabledRecalculated = isDisabled

        if (originalSettings) {
            const { hour, quantity } = originalSettings
            const isActuallySelectedOption = option === hour
            const seatsModulo = recalculatedOptionSeats % 10

            // add selected tickets to hour available limit if we are editing quantity on the same already selected course
            if (isActuallySelectedOption) {
                const optionInfos = defineMessages({
                    one: {
                        id: 'upsells.transport.hour_option_one',
                        defaultMessage: 'Zostało {seats} miejsce',
                    },
                    many24: {
                        id: 'upsells.transport.hour_option_many_2_4',
                        defaultMessage: 'Zostały {seats} miejsca',
                    },
                    many51: {
                        id: 'upsells.transport.hour_option_many_5_1',
                        defaultMessage: 'Zostało {seats} miejsc',
                    },
                })

                let matchedInfo = optionInfos.one

                if (seatsModulo >= 2 && seatsModulo <= 4)
                    matchedInfo = optionInfos.many24
                else if (seatsModulo <= 1 || seatsModulo >= 5)
                    matchedInfo = optionInfos.many51

                isDisabledRecalculated = false
                recalculatedOptionSeats += quantity
                recalculatedOptionInfo = formatMessage(matchedInfo, {
                    seats: recalculatedOptionSeats,
                })
            }
        }

        return {
            option,
            isDisabled:
                isDisabledRecalculated ||
                recalculatedOptionSeats < selectedTicketsAmount,
            optionInfo: recalculatedOptionInfo,
        }
    })

    const NoMoreSeatsMsg = (
        <Info color="error">
            {formatMessage({
                id: 'upsells.transport.msg_no_more_seats',
                defaultMessage:
                    'W tym dniu nie ma już wolnych miejsc w autobusie. Wybierz inną datę.',
            })}
        </Info>
    )

    return isNoTickets || !optionsWithRecalculatedAvailability.length ? (
        NoMoreSeatsMsg
    ) : (
        <Select
            id={id}
            options={optionsWithRecalculatedAvailability}
            placeholder={selectorPlaceholder}
            value={hour}
            onChange={handleChange}
            isHoursSelect={true}
            testId={`${testIdPrefix ?? 'transport'}-${routeIndex}-select-${id}`}
        />
    )
}

export default BusHours
